'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { DialogTrigger } from '@radix-ui/react-dialog';
import NoImage from '@/components/customercenter-ui/svgs/noImage';
import { useCart } from '@/frontastic';
import { CurrencyHelpers } from '@/helpers/utils/currencyHelpers';
import { getAgentName } from '@/helpers/utils/siteUtils';
import { ADDITIONAL_DETAILS_NAME, ADDITIONAL_DETAILS_TYPE, numericRegex } from '../../../../../constants/constant';
import ContentStackImage from '../../../common/contentStackImage/contentStackImage';
import { ADJUST_PRICE_REASONS, ADJUST_PRICE_TABLE_HEADER, ADJUST_PRICE_TYPES } from '../../cart/constants';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/customSelect/customSelect';
import { Table, TableColumnConfig } from '../../ui/customTable/Table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import Loader from '../../ui/loader';

const AdjustPriceModal = ({ CartItem: liveCartItem, Texts, price, setPrice }: any) => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [CartItem, setCartItem] = useState(liveCartItem);
  const { adjustLineItemPrice, addAdditionalDetails } = useCart(false);
  const [error, setError] = useState({ price: '', reason: '' });
  const [reason, setReason] = useState('');
  const [adjustmentType, setAdjustmentType] = useState(ADJUST_PRICE_TYPES.ITEM_PRICE);
  const [discount, setDiscount] = useState('');

  const isDiscount = adjustmentType !== ADJUST_PRICE_TYPES.ITEM_PRICE;
  const isDollarOff = adjustmentType === ADJUST_PRICE_TYPES.DOLLAR_OFF;
  const centFactor = 10 ** (CartItem?.price?.fractionDigits ?? 2);
  const itemPrice = (CartItem?.price?.centAmount ?? 0) / centFactor;
  const discountValue = parseFloat(discount);
  const isDiscountValid = isDollarOff
    ? discountValue > 0 && discountValue < itemPrice
    : discountValue > 0 && discountValue <= 100;
  const discountedPrice =
    Math.round((isDollarOff ? itemPrice - discountValue : itemPrice * (1 - discountValue / 100)) * centFactor) /
    centFactor;

  const getDefaultPrice = (item = CartItem) => CurrencyHelpers.customCurrencyFormat(item?.price)?.split('$')?.[1];

  const tableData = [
    {
      ...CartItem,
      productDetails: CartItem?.name,
      itemPrice: CurrencyHelpers.customCurrencyFormat(CartItem?.price),
      totalPrice: CurrencyHelpers.customCurrencyFormat(CartItem?.totalPrice),
      originalPrice: CurrencyHelpers.customCurrencyFormat(CartItem?.originalPrice),
      actions: Texts.selectReason,
    },
  ];

  useEffect(() => {
    if (isDiscount) {
      const discountError = isDollarOff ? Texts.dollarOffInvalid : Texts.percentOffInvalid;
      setError((prev) => ({ ...prev, price: discount && !isDiscountValid ? discountError : '' }));
      return;
    }
    const oldPrice = parseFloat(CurrencyHelpers.customCurrencyFormat(CartItem?.originalPrice)?.split('$')?.[1]);
    const newPrice = parseFloat(price);
    if (newPrice > oldPrice) {
      setError((prev) => ({ ...prev, price: Texts.itemPriceExceed }));
    } else {
      setError({ reason: '', price: '' });
    }
  }, [price, adjustmentType, discount]);

  const tableHeaders: TableColumnConfig<any>[] = [
    {
      key: ADJUST_PRICE_TABLE_HEADER.PRODUCT_DETAILS.KEY,
      header: ADJUST_PRICE_TABLE_HEADER.PRODUCT_DETAILS.HEADER,
      render: (data, rowData) => (
        <section className="flex">
          {rowData?.productImg ? (
            <Link prefetch={false} href={rowData?._url || '#'} passHref title={rowData?.name ?? ''}>
              <ContentStackImage
                className={'h-32 w-32 border'}
                src={rowData?.productImg}
                imageWidth={120}
                imageHeight={120}
                alt={`Product Image ${rowData?.lineItemId}`}
                loading="eager"
              />
            </Link>
          ) : (
            <NoImage width={120} height={120} />
          )}
          <section className="ml-4">
            <Link prefetch={false} href={rowData?._url || '#'} passHref title={rowData?.name ?? ''}>
              <p className="max-w-md text-lg font-bold hover:underline">{rowData?.name}</p>
            </Link>
            <p className="text-sm font-regular">
              {Texts.itemNumber} {rowData?.itemNumber}
            </p>
            <p className="text-sm font-regular">{rowData?.color}</p>
            <p className="text-sm font-regular">
              {Texts.qty}: {rowData?.count}
            </p>
          </section>
        </section>
      ),
    },
    {
      key: ADJUST_PRICE_TABLE_HEADER.ITEM_PRICE.KEY,
      header: ADJUST_PRICE_TABLE_HEADER.ITEM_PRICE.HEADER,
      render: () => (
        <section className="flex flex-col items-baseline gap-3">
          <Select
            value={adjustmentType}
            onValueChange={(value) => {
              setAdjustmentType(value);
              setDiscount('');
              setPrice(getDefaultPrice());
              setError((prev) => ({ ...prev, price: '' }));
            }}
          >
            <SelectTrigger className="w-36 border-foggyGray text-black">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="w-36 bg-white px-4">
              {Object.values(ADJUST_PRICE_TYPES).map((type) => (
                <SelectItem value={type} key={`KEY-${type}`}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <section className="flex items-center gap-2">
            {adjustmentType !== ADJUST_PRICE_TYPES.PERCENT_OFF && <div>{Texts.currency}</div>}
            <input
              value={isDiscount ? discount : price}
              type="text"
              maxLength={10}
              onChange={(e) => {
                const inputValue = e.target.value;
                const regex = numericRegex;
                if (regex.test(inputValue) && inputValue.length <= 10) {
                  if (isDiscount) {
                    setDiscount(inputValue);
                  } else {
                    setPrice(inputValue);
                  }
                }
              }}
              onBlur={(e) => {
                if (adjustmentType === ADJUST_PRICE_TYPES.PERCENT_OFF) return;
                const inputValue = e.target.value?.trim();
                const numericValue = Number(inputValue);
                if (!inputValue || isNaN(numericValue)) return;
                const formattedValue = numericValue.toFixed(2);
                if (formattedValue === inputValue) return;
                if (isDollarOff) {
                  setDiscount(formattedValue);
                } else {
                  setPrice(formattedValue);
                }
              }}
              className={`gbunmask peer block h-10 w-32 shrink-0 appearance-none rounded-md px-3 ${
                error?.price ? 'border-scarletRed text-scarletRed' : 'outline-none'
              }`}
            />
            {adjustmentType === ADJUST_PRICE_TYPES.PERCENT_OFF && <div>%</div>}
          </section>
          {adjustmentType === ADJUST_PRICE_TYPES.PERCENT_OFF && isDiscountValid && (
            <p className="-mt-2 w-32 text-center text-sm">
              {CurrencyHelpers.customCurrencyFormat({
                ...CartItem?.price,
                centAmount: (CartItem?.price?.centAmount ?? 0) - Math.round(discountedPrice * centFactor),
              })}
            </p>
          )}
          {error?.price && <p className="max-w-[10rem] font-semibold text-scarletRed">{error.price}</p>}
        </section>
      ),
    },
    {
      key: ADJUST_PRICE_TABLE_HEADER.TOTAL_PRICE.KEY,
      header: ADJUST_PRICE_TABLE_HEADER.TOTAL_PRICE.HEADER,
      render: (data) => (
        <div className="w-20">
          {isDiscount && isDiscountValid ? (
            <>
              <p className="line-through">{data}</p>
              <p className="font-semibold text-scarletRed">
                {CurrencyHelpers.customCurrencyFormat({
                  ...CartItem?.price,
                  centAmount: Math.round(discountedPrice * centFactor) * (CartItem?.count ?? 1),
                })}
              </p>
            </>
          ) : (
            data
          )}
        </div>
      ),
    },
    {
      key: ADJUST_PRICE_TABLE_HEADER.REASONS.KEY,
      header: ADJUST_PRICE_TABLE_HEADER.REASONS.HEADER,
      render: (data, rowData) => (
        <section className="flex flex-col items-baseline justify-start gap-2">
          <Select
            defaultValue={rowData.value}
            onValueChange={(value) => {
              setError((prev) => ({ ...prev, reason: '' }));
              setReason(value);
            }}
          >
            <SelectTrigger
              className={`mr-2 w-52 ${
                error?.reason ? 'border-scarletRed text-scarletRed' : 'border-foggyGray text-black'
              }`}
            >
              <SelectValue placeholder={Texts.selectReason} />
            </SelectTrigger>
            <SelectContent className="w-52 bg-white px-4">
              {ADJUST_PRICE_REASONS?.map((reason) => (
                <SelectItem value={reason?.value} key={`KEY-${reason?.value}`}>
                  {reason?.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="font-semibold text-scarletRed">{error?.reason}</p>
        </section>
      ),
    },
  ];

  const onSubmit = async () => {
    if (!(isDiscount ? discount : price)) {
      return setError((prev) => ({ ...prev, price: Texts.valueError }));
    } else if (!reason) {
      return setError((prev) => ({ ...prev, reason: Texts.selectAReason }));
    }
    if (error?.price || error?.reason) {
      return;
    }
    setLoading(true);
    const payload = {
      lineItemId: CartItem?.lineItemId,
      amount: isDiscount ? discountedPrice : parseFloat(price),
    };
    const reasonPayload = {
      name: ADDITIONAL_DETAILS_NAME.ADJUST_PRICE,
      value: reason,
      type: ADDITIONAL_DETAILS_TYPE.REASONS,
      agentName: getAgentName()?.replace(/@.*/, ''),
    };
    await adjustLineItemPrice(payload);
    await addAdditionalDetails(reasonPayload);
    setLoading(false);
    setOpen(false);
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (value) {
          setCartItem(liveCartItem);
          setPrice(getDefaultPrice(liveCartItem));
          setError({ price: '', reason: '' });
          setReason('');
          setAdjustmentType(ADJUST_PRICE_TYPES.ITEM_PRICE);
          setDiscount('');
        }
      }}
    >
      <DialogTrigger>{Texts.adjustPrice}</DialogTrigger>
      <DialogContent className="max-w-[900px] bg-white sm:rounded-md">
        {loading && <Loader />}
        <DialogHeader>
          {/* chng-text-25px */}
          <DialogTitle className="text-2xl font-bold">
            <div className="text-2xl font-bold">{Texts.adjustItems}</div>
          </DialogTitle>
        </DialogHeader>
        <Table columns={tableHeaders} data={tableData} />
        <section className="flex justify-start gap-4">
          <Button className="rounded-md bg-deepBlue p-4 text-white" type="submit" onClick={onSubmit}>
            {Texts.addAdjustments}
          </Button>
          <Button className="p-0 underline" onClick={() => setOpen(false)} type="reset">
            {Texts.cancel}
          </Button>
        </section>
      </DialogContent>
    </Dialog>
  );
};

export default AdjustPriceModal;
